import { useMemo, useState } from 'react';
import {
  chanceEventDefaults,
  chanceEventsDefaults,
  chanceRulesDefaults,
} from '../../../lib/s2/defaults';
import { buildPathMatrix, optionsWithoutPayoff, type PathMatrix } from '../../../lib/s2/pathMatrix';
import {
  CHANCE_TRIGGER_KINDS,
  type ChanceEventDef,
  type ChanceEventsDef,
  type ChanceRulesDef,
} from '../../../lib/s2/types';
import { OUTCOME_TONES, type BeatChainDef } from '../../../lib/story/types';
import { TONE_COLORS } from '../../../lib/story/ui';
import { withDefaults } from '../../../lib/teamsDefaults';
import { issuesUnder, type FieldErrors } from '../../../lib/validate';
import {
  CheckboxField,
  Fieldset,
  IssueList,
  NumberField,
  SelectField,
  StringListEditor,
  TextField,
  smallBtn,
} from '../fields';
import { useContentBundle } from '../refs';
import type { FormProps } from '../types';

type Rules = Required<ChanceRulesDef>;
type Ev = Required<ChanceEventDef>;

/** An event with its C# defaults, nested blocks too. */
function chanceEventWithDefaults(e: ChanceEventDef | null | undefined): Ev {
  const d = chanceEventDefaults();
  const v = withDefaults(d, (e ?? {}) as Ev);
  const trig = withDefaults(d.trigger, v.trigger ?? d.trigger);
  return {
    ...v,
    eligibility: withDefaults(d.eligibility, v.eligibility ?? d.eligibility),
    trigger: { ...trig, room: withDefaults(d.trigger.room!, trig.room ?? d.trigger.room!) },
  };
}

/**
 * The `chanceEvents` section (chance.html §4 Admin): the rules form, and an event list with eligibility and trigger forms. The cells,
 * choices, cast and hidden vars are the engine chain the event names (M10), edited in the beat editor; the path matrix below shows the
 * selected event's chain: every option against every hidden roll.
 */
export function ChanceEventsForm({ value, onChange, errors }: FormProps<ChanceEventsDef>) {
  const d = chanceEventsDefaults();
  const v = withDefaults(d, (value ?? {}) as Required<ChanceEventsDef>);
  const rules = withDefaults(chanceRulesDefaults(), (v.rules ?? {}) as Rules);
  const events = (v.events ?? []).map(chanceEventWithDefaults);
  const [sel, setSel] = useState(0);
  const set = (next: Partial<Required<ChanceEventsDef>>) =>
    onChange({ ...v, rules, events, ...next });
  const setRule = <K extends keyof Rules>(k: K, x: Rules[K]) =>
    set({ rules: { ...rules, [k]: x } });
  const setEvent = (i: number, e: Ev) => set({ events: events.map((x, j) => (j === i ? e : x)) });
  const current = events[sel];

  const ruleNum = (k: keyof Rules & string, label: string, hint: string, integer = true) => (
    <NumberField
      label={label}
      hint={hint}
      integer={integer}
      min={0}
      step={integer ? 1 : 0.01}
      value={rules[k] as number}
      issues={errors[`rules.${k}`]}
      onChange={(x) => setRule(k, (x ?? 0) as never)}
    />
  );

  return (
    <>
      <Fieldset legend="Rules: how often">
        <CheckboxField
          label="Enabled"
          checked={!!rules.enabled}
          onChange={(x) => setRule('enabled', x)}
          hint="Off until content turns it on: no event fires while this is off."
        />
        {ruleNum('chancePerRaid', 'Chance per raid', 'The roll per eligible raid, 0..1.', false)}
        {ruleNum('maxPerRaid', 'Max per raid', 'Never 2 in a raid at the starting value.')}
        {ruleNum('maxPerRunDay', 'Max per run-day', '')}
        {ruleNum('minRaidsBetween', 'Raids between', 'Raids that must pass after one fires.')}
        {ruleNum('skipFirstRaids', 'Skip first raids', "A run's first raids never get one.")}
      </Fieldset>

      <div className="mb-4 rounded border-2 border-ink bg-white p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold">Events</h3>
          <button
            type="button"
            className={smallBtn}
            onClick={() => {
              set({
                events: [
                  ...events,
                  chanceEventWithDefaults({ id: `event_${events.length + 1}`, name: 'New event' }),
                ],
              });
              setSel(events.length);
            }}
          >
            Add event
          </button>
        </div>
        <IssueList issues={errors.events} />
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase">
              <th>Id</th>
              <th>Chain</th>
              <th>Weight</th>
              <th>Trigger</th>
              <th>Squad</th>
              <th aria-label="Problems" />
            </tr>
          </thead>
          <tbody>
            {events.map((e, i) => {
              const n = issuesUnder(errors, `events.${i}`).length;
              return (
                <tr
                  key={i}
                  onClick={() => setSel(i)}
                  className={`cursor-pointer border-t border-newsprint ${i === sel ? 'bg-benday/30' : 'hover:bg-caption/20'}`}
                >
                  <td className="font-mono">{e.id}</td>
                  <td className="font-mono">{e.chainId}</td>
                  <td>{e.weight}</td>
                  <td>{e.trigger.kind}</td>
                  <td>
                    {(e.eligibility.squadMin ?? 1) > 1 ? `${e.eligibility.squadMin}+` : 'any'}
                  </td>
                  <td className="text-right text-xs text-blam">{n > 0 ? `● ${n}` : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {current && (
        <ChanceEventEditor
          key={sel}
          event={current}
          index={sel}
          errors={errors}
          onChange={(e) => setEvent(sel, e)}
          onRemove={() => {
            if (!confirm(`Remove event "${current.id}"?`)) return;
            set({ events: events.filter((_, j) => j !== sel) });
            setSel(Math.max(0, sel - 1));
          }}
        />
      )}
    </>
  );
}

function ChanceEventEditor({
  event: e,
  index,
  errors,
  onChange,
  onRemove,
}: {
  event: Ev;
  index: number;
  errors: FieldErrors;
  onChange: (e: Ev) => void;
  onRemove: () => void;
}) {
  const b = useContentBundle();
  const base = `events.${index}`;
  const chains = (b.beatChains ?? []).filter((c) => c?.id);
  const setPieces = (b.setPieces ?? []).filter((s) => s?.id);
  const chain = chains.find((c) => c.id === e.chainId);
  const elig = e.eligibility;
  const trig = e.trigger;
  return (
    <>
      <Fieldset legend={`Event ${e.id || index + 1}`}>
        <TextField
          label="Id"
          mono
          value={e.id}
          issues={errors[`${base}.id`]}
          onChange={(x) => onChange({ ...e, id: x })}
        />
        <TextField
          label="Name"
          value={e.name}
          issues={errors[`${base}.name`]}
          onChange={(x) => onChange({ ...e, name: x })}
        />
        <SelectField
          label="Beat chain"
          hint="The engine chain it plays; its root beat is the entry. Edit cells and choices in the beat editor."
          value={e.chainId}
          options={chains.map((c) => ({ value: c.id!, label: `${c.title || c.id} (${c.id})` }))}
          issues={errors[`${base}.chainId`]}
          onChange={(x) => onChange({ ...e, chainId: x ?? '' })}
        />
        <div className="flex items-end">
          <a
            href={`/content/beat-chains${e.chainId ? `?id=${encodeURIComponent(e.chainId)}` : ''}`}
            className="text-sm underline"
          >
            Open the beat editor
          </a>
        </div>
        <NumberField
          label="Weight"
          integer
          min={0}
          value={e.weight}
          issues={errors[`${base}.weight`]}
          onChange={(x) => onChange({ ...e, weight: x ?? 0 })}
        />
        <CheckboxField
          label="Once per run"
          checked={!!e.oncePerRun}
          onChange={(x) => onChange({ ...e, oncePerRun: x })}
        />
      </Fieldset>
      <Fieldset legend="Eligibility">
        <StringListEditor
          label="Raid modes"
          hint="team, in_person, ...; empty = any non-boss mode."
          value={elig.modes ?? []}
          errors={errors}
          base={`${base}.eligibility.modes`}
          onChange={(x) => onChange({ ...e, eligibility: { ...elig, modes: x } })}
        />
        <NumberField
          label="Squad min"
          hint="2 for the squad-only events; 1 = solo is fine."
          integer
          min={1}
          value={elig.squadMin}
          issues={errors[`${base}.eligibility.squadMin`]}
          onChange={(x) => onChange({ ...e, eligibility: { ...elig, squadMin: x ?? 1 } })}
        />
        <StringListEditor
          label="Building categories"
          hint="Category ids, or * for any."
          value={elig.categories ?? []}
          errors={errors}
          base={`${base}.eligibility.categories`}
          onChange={(x) => onChange({ ...e, eligibility: { ...elig, categories: x } })}
        />
      </Fieldset>
      <Fieldset legend="Trigger">
        <SelectField
          label="Kind"
          value={trig.kind}
          options={CHANCE_TRIGGER_KINDS.map((k) => ({ value: k, label: k }))}
          issues={errors[`${base}.trigger.kind`]}
          onChange={(x) => onChange({ ...e, trigger: { ...trig, kind: x ?? 'enter_room' } })}
        />
        <NumberField
          label="Not before (raid s)"
          integer
          min={0}
          value={trig.minRaidS}
          issues={errors[`${base}.trigger.minRaidS`]}
          onChange={(x) => onChange({ ...e, trigger: { ...trig, minRaidS: x ?? 0 } })}
        />
        {trig.kind === 'enter_room' && (
          <>
            <NumberField
              label="Room min area (m²)"
              min={0}
              value={trig.room?.minAreaM2}
              issues={errors[`${base}.trigger.room.minAreaM2`]}
              onChange={(x) =>
                onChange({ ...e, trigger: { ...trig, room: { ...trig.room, minAreaM2: x ?? 0 } } })
              }
            />
            <CheckboxField
              label="Never the entry room"
              checked={!!trig.room?.notEntry}
              onChange={(x) =>
                onChange({ ...e, trigger: { ...trig, room: { ...trig.room, notEntry: x } } })
              }
            />
          </>
        )}
        {trig.kind === 'pass_set_piece' && (
          <SelectField
            label="Set piece"
            value={trig.setPieceId}
            options={setPieces.map((s) => ({ value: s.id!, label: `${s.name || s.id} (${s.id})` }))}
            issues={errors[`${base}.trigger.setPieceId`]}
            onChange={(x) => onChange({ ...e, trigger: { ...trig, setPieceId: x ?? '' } })}
          />
        )}
        <div className="sm:col-span-2">
          <button type="button" className={`${smallBtn} text-blam`} onClick={onRemove}>
            Remove this event
          </button>
        </div>
      </Fieldset>
      {chain ? (
        <PathMatrixView chain={chain} />
      ) : (
        <p className="text-sm text-bruise">Pick a beat chain to see its path matrix.</p>
      )}
    </>
  );
}

const pct = (p: number) => `${Math.round(p * 1000) / 10}%`;

/** The path matrix: per beat and option, a row per hidden-roll combination, with the outcomes, tones, effects and odds. */
export function PathMatrixView({ chain }: { chain: BeatChainDef }) {
  const m: PathMatrix = useMemo(() => buildPathMatrix(chain), [chain]);
  const missing = optionsWithoutPayoff(m);
  return (
    <section className="mb-4 rounded border-2 border-ink bg-white p-3" aria-label="Path matrix">
      <h3 className="text-sm font-bold">Path matrix: {chain.title || chain.id}</h3>
      <p className="text-xs text-bruise">
        Every option × every hidden roll (each var from its default case). Tells don&apos;t change
        outcomes. A deeper layer counts toward &quot;pays off&quot;.
      </p>
      {missing.length > 0 && (
        <p className="mt-1 text-xs font-semibold text-blam" role="alert">
          No good or windfall path and no toneNote:{' '}
          {missing.map((o) => `${o.beatId}/${o.optionId}`).join(', ')}
        </p>
      )}
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left uppercase">
              <th className="pr-2">Beat</th>
              <th className="pr-2">Option</th>
              {m.vars.map((v) => (
                <th key={v} className="pr-2 font-mono normal-case">
                  {v}
                </th>
              ))}
              <th className="pr-2">P(roll)</th>
              <th>Outcomes</th>
            </tr>
          </thead>
          <tbody>
            {m.options.map((o) =>
              o.rolls.map((r, ri) => (
                <tr
                  key={`${o.beatId}/${o.optionId}/${ri}`}
                  className={ri === 0 ? 'border-t-2 border-ink' : 'border-t border-newsprint'}
                >
                  {ri === 0 && (
                    <>
                      <td rowSpan={o.rolls.length} className="pr-2 align-top font-mono">
                        {o.beatId}
                      </td>
                      <td rowSpan={o.rolls.length} className="pr-2 align-top">
                        <span className="font-semibold">{o.optionId || '-'}</span> {o.label}
                        {o.requires && <div className="text-bruise">only if: {o.requires}</div>}
                        {o.toneNote && <div className="italic text-bruise">note: {o.toneNote}</div>}
                        <div>
                          {o.canPayOff ? (
                            <span className="text-benday">can pay off</span>
                          ) : (
                            <span className="text-blam">no payoff</span>
                          )}
                          {' · '}
                          {o.canGoWrong ? (
                            'can go wrong'
                          ) : (
                            <span className="text-bruise">never goes wrong</span>
                          )}
                        </div>
                        <MixBar mix={o.mix} />
                      </td>
                    </>
                  )}
                  {m.vars.map((v) => (
                    <td key={v} className="pr-2 font-mono">
                      {r.vars[v] ?? ''}
                    </td>
                  ))}
                  <td className="pr-2">{pct(r.p)}</td>
                  <td>
                    {r.outcomes.length === 0 && <span className="text-blam">no outcome</span>}
                    {r.outcomes.map((x) => (
                      <div key={x.outcomeId}>
                        <span
                          className="mr-1 inline-block rounded px-1 font-semibold"
                          style={{ background: x.tone ? TONE_COLORS[x.tone] : undefined }}
                        >
                          {x.tone || '?'}
                        </span>
                        <span className={`font-mono ${x.missing ? 'text-blam' : ''}`}>
                          {x.outcomeId}
                        </span>{' '}
                        {pct(x.p)}
                        {x.next && <span className="text-bruise"> → {x.next}</span>}
                        {x.effects.length > 0 && (
                          <span className="text-bruise"> · {x.effects.join('; ')}</span>
                        )}
                      </div>
                    ))}
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MixBar({ mix }: { mix: Record<string, number> }) {
  const total = OUTCOME_TONES.reduce((s, t) => s + (mix[t] ?? 0), 0) || 1;
  return (
    <div
      className="mt-1 flex h-2 w-32 overflow-hidden rounded border border-ink"
      title="Tone mix of this option"
    >
      {OUTCOME_TONES.map((t) => (
        <div
          key={t}
          style={{ width: `${((mix[t] ?? 0) / total) * 100}%`, background: TONE_COLORS[t] }}
        />
      ))}
    </div>
  );
}
